import * as Schema from "effect/Schema";
import { TrimmedNonEmptyString } from "./baseSchemas.ts";

const FILESYSTEM_PATH_MAX_LENGTH = 512;

export const FilesystemBrowseRoot = Schema.Literal("windows_drives");
export type FilesystemBrowseRoot = typeof FilesystemBrowseRoot.Type;

const FilesystemBrowsePathInput = Schema.Struct({
  partialPath: TrimmedNonEmptyString.check(Schema.isMaxLength(FILESYSTEM_PATH_MAX_LENGTH)),
  cwd: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(FILESYSTEM_PATH_MAX_LENGTH))),
});

const FilesystemBrowseRootInput = Schema.Struct({
  root: FilesystemBrowseRoot,
});

export const FilesystemBrowseInput = Schema.Union([
  FilesystemBrowsePathInput,
  FilesystemBrowseRootInput,
]);
export type FilesystemBrowseInput = typeof FilesystemBrowseInput.Type;

export const FilesystemBrowseEntry = Schema.Struct({
  name: TrimmedNonEmptyString,
  fullPath: TrimmedNonEmptyString,
});
export type FilesystemBrowseEntry = typeof FilesystemBrowseEntry.Type;

const FilesystemBrowsePathResult = Schema.Struct({
  parentPath: TrimmedNonEmptyString,
  entries: Schema.Array(FilesystemBrowseEntry),
});

const FilesystemBrowseRootResult = Schema.Struct({
  root: FilesystemBrowseRoot,
  entries: Schema.Array(FilesystemBrowseEntry),
});

export const FilesystemBrowseResult = Schema.Union([
  FilesystemBrowsePathResult,
  FilesystemBrowseRootResult,
]);
export type FilesystemBrowseResult = typeof FilesystemBrowseResult.Type;

export const FilesystemBrowseFailure = Schema.Literals([
  "windows_path_unsupported",
  "windows_drive_root_unsupported",
  "current_project_required",
  "read_directory_failed",
]);
export type FilesystemBrowseFailure = typeof FilesystemBrowseFailure.Type;

function decodedFilesystemBrowseErrorMessage(props: object): string | undefined {
  if (!("message" in props)) return undefined;
  return typeof props.message === "string" ? props.message : undefined;
}

export class FilesystemBrowseError extends Schema.TaggedErrorClass<FilesystemBrowseError>()(
  "FilesystemBrowseError",
  {
    partialPath: Schema.optional(TrimmedNonEmptyString),
    root: Schema.optional(FilesystemBrowseRoot),
    cwd: Schema.optional(TrimmedNonEmptyString),
    failure: Schema.optional(FilesystemBrowseFailure),
    parentPath: Schema.optional(TrimmedNonEmptyString),
    platform: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // Structured diagnostics stay optional for rolling compatibility with legacy message-only
  // payloads, while new call sites must provide the request context and failure classification.
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: {
    readonly partialPath?: string;
    readonly root?: FilesystemBrowseRoot;
    readonly cwd?: string | undefined;
    readonly failure: FilesystemBrowseFailure;
    readonly parentPath?: string;
    readonly platform?: string;
    readonly cause?: unknown;
  }) {
    const cwd = props.cwd === undefined ? "" : ` from '${props.cwd}'`;
    const browseTarget =
      props.partialPath === undefined
        ? `filesystem root '${props.root ?? "unknown"}'`
        : `filesystem path '${props.partialPath}'`;
    super({
      ...props,
      message:
        decodedFilesystemBrowseErrorMessage(props) ?? `Failed to browse ${browseTarget}${cwd}.`,
    } as any);
  }
}
